import { isPlatformBrowser } from '@angular/common';
import {
   Component,
   Inject,
   OnInit,
   PLATFORM_ID,
   TemplateRef,
   ViewChild,
   OnDestroy
} from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { catchError, interval, map, of, Subscription, takeUntil, Subject } from 'rxjs';
import { Router, NavigationEnd } from '@angular/router';
import { TypesenseService } from '../../services/typesense.service';
import { parseApiDateToUtc } from 'src/app/utils/date-utils';
import { filter } from 'rxjs/operators';

interface CountdownTime {
   days: number;
   hours: number;
   minutes: number;
   seconds: number;
}

@Component({
    selector: 'app-pre-trading-popup',
    templateUrl: './pre-trading-popup.component.html',
    styleUrls: ['./pre-trading-popup.component.scss'],
    standalone: false
})
export class PreTradingPopupComponent implements OnInit, OnDestroy {
   private destroy$ = new Subject<void>();
   private countdownSubscription?: Subscription;
   private modalRef?: NgbModalRef;
   isBrowser = false;

   @ViewChild('preTradingModal') preTradingModal!: TemplateRef<any>;

   countdown: CountdownTime = { days: 0, hours: 0, minutes: 0, seconds: 0 };

   preTradingData = {
      currentPeople: 0,
      maxPeople: 100
   };

   endDate = '2025-11-16 03:59:59'; // fallback

   constructor(
      private modalService: NgbModal,
      private typesenseService: TypesenseService,
      @Inject(PLATFORM_ID) private platformId: Object,
      private router: Router
   ) {
      this.isBrowser = isPlatformBrowser(this.platformId);
   }

   ngOnInit(): void {
      if (!this.isBrowser) return;

      // ✅ Recheck popup eligibility on each navigation
      this.router.events
         .pipe(
            filter((e): e is NavigationEnd => e instanceof NavigationEnd),
            takeUntil(this.destroy$)
         )
         .subscribe(() => this.checkAndOpenPopup());

      // initial load
      // this.checkAndOpenPopup();
   }

   ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
      this.cleanupSubscriptions();
   }

   /** ✅ Cleanly closes modal and stops timers/subscriptions */
   private cleanupSubscriptions(): void {
      this.countdownSubscription?.unsubscribe();
      this.modalRef?.close();
      this.modalRef = undefined;
   }

   /** ✅ Detect if current URL should block popup */
   private isExcludedRoute(url: string): boolean {
      const path = url.split('?')[0].toLowerCase(); // remove query string
    
      const excludedPrefixes = [
        '/stock/',
        '/etf/',
        '/supported-countries', 
        '/supported-brokerages',
        '/investing-coverage',
      ];
    
      return excludedPrefixes.some(prefix => path.startsWith(prefix));
   }

   /** ✅ Main logic to decide whether to open popup */
   private checkAndOpenPopup(): void {
      if (!this.isBrowser) return;

      const currentUrl = this.router.url;

      // Prevent popup on excluded routes
      if (this.isExcludedRoute(currentUrl)) {
         this.cleanupSubscriptions(); // ensure no modal remains open
         return;
      }
   
      const hasShown = sessionStorage.getItem('preTradingModalShown');
      if (hasShown) return; // already shown once this session
   
      // Prevent double open attempts
      if (this.modalRef) return;
   
      this.typesenseService.getBannersVisibilityData()
         .pipe(
            takeUntil(this.destroy$),
            catchError(() => of({ popupBanner: false }))
         )
         .subscribe((response: any) => {
            const eligible =
               response?.popupBanner &&
               !this.isExcludedRoute(this.router.url);
   
            if (eligible) {
               // Let the modal open safely after 3 sec
               setTimeout(() => {
                  if (!this.modalRef && !this.isExcludedRoute(this.router.url)) {
                     this.openPreTradingModal();
                  }
               }, 3000);
            }
         });
   }
   

   /** ✅ Opens the modal safely */
   openPreTradingModal(): void {
      if (!this.isBrowser || this.modalRef || this.isExcludedRoute(this.router.url)) return;

      this.modalRef = this.modalService.open(this.preTradingModal, {
         centered: true,
         backdrop: 'static',
         size: 'lg',
         scrollable: true,
         windowClass: 'no-border',
         backdropClass: 'popup-backdrop'
      });

      sessionStorage.setItem('preTradingModalShown', 'true');

      this.startCountdown();
   }

   /** ✅ Closes modal manually */
   closePreTradingModal(): void {
      this.cleanupSubscriptions();
   }

   /** ⏱ Fetch promo data + countdown info */
   private startCountdown(): void {
      this.typesenseService
         .fetchCrowdfundingProgressData3()
         .pipe(
            takeUntil(this.destroy$),
            map((response) => {
               // ✅ Parse promo data
               this.preTradingData = {
                  currentPeople: response?.pretrading_cmp_current_people || 0,
                  maxPeople: response?.pretrading_cmp_max_people || 100
               };

               // ✅ Parse end date or fallback
               const apiDate = response?.['pretrading_cmp_end_date'] || this.endDate;
               return parseApiDateToUtc(apiDate);
            }),
            catchError((err) => {
               console.error('Failed to fetch promo data, using fallback', err);
               this.preTradingData = { currentPeople: 3, maxPeople: 100 };
               return of(parseApiDateToUtc(this.endDate));
            })
         )
         .subscribe((endDate) => {
            if (endDate) this.startTimer(endDate);
         });
   }

   /** ⏳ Countdown timer logic */
   private startTimer(endDate: Date): void {
      this.countdownSubscription?.unsubscribe(); // clear old timer first

      this.countdownSubscription = interval(1000)
         .pipe(takeUntil(this.destroy$))
         .subscribe(() => {
            const now = new Date().getTime();
            const distance = endDate.getTime() - now;

            if (distance > 0) {
               this.countdown = {
                  days: Math.floor(distance / (1000 * 60 * 60 * 24)),
                  hours: Math.floor(
                     (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
                  ),
                  minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
                  seconds: Math.floor((distance % (1000 * 60)) / 1000)
               };
            } else {
               this.countdown = { days: 0, hours: 0, minutes: 0, seconds: 0 };
               this.closePreTradingModal(); // auto-close when expired
            }
         });
   }
}